import { ActionIcon, Card, Group, Menu, Stack, Box, Text } from '@mantine/core';
import { OrganizationContact } from '@medplum/fhirtypes';
import { IconDots, IconPhone, IconTrash, IconPlus } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { FileInput } from '@mantine/core';
import {
  inferSystem,
  setContactTelecomAt,
  addContactTelecomEntry,
  removeContactTelecomAt,
} from '../../services/organization';
import classes from './PartnerEditDrawer.module.css';
import { ORGANIZATION_CONTACT_TITLE_EXTENSION, ORGANIZATION_CONTACT_PHOTO_EXTENSION } from '@bariendo/lib/extensions';
import { SystemIcon } from './SystemIcon';
import { PartnerContactAvatar } from './PartnerContactAvatar';
import { EditableField, FaxEditableField, SmartEditableField } from '@/components/EditableField';
import { ConditionalLayout } from '@/components/ConditionalLayout';
import { TelecomEntry } from './TelecomEntry';

function getTitle(contact: OrganizationContact) {
  return contact.extension?.find((e) => e.url === ORGANIZATION_CONTACT_TITLE_EXTENSION)?.valueString;
}

export function PartnerContactCard({
  contact,
  onDelete,
  onChange,
}: {
  contact: OrganizationContact;
  onDelete: () => void;
  onChange: (contact: OrganizationContact) => void;
}) {
  const medplum = useMedplum();

  const [{ given, family }, setName] = useState({
    given: contact.name?.given?.[0] || 'Unknown',
    family: contact.name?.family || '',
  });
  const [title, setTitle] = useState(getTitle(contact));
  const telecom = contact.telecom || [];
  const faxTelecoms = telecom.filter((t) => t.system === 'fax');
  const nonFaxTelecom = telecom.filter((t) => t.system !== 'fax');
  const [expandingFax, setExpandingFax] = useState(false);
  const [newFaxValue, setNewFaxValue] = useState('');

  const avatarInputRef = useRef<HTMLButtonElement>(null);

  const [addingNew, setAddingNew] = useState(false);
  const [newValue, setNewValue] = useState('');

  const addNewEntry = (value: string) => {
    onChange(addContactTelecomEntry(contact, value));
    setAddingNew(false);
    setNewValue('');
  };

  const deleteTelecomAt = (index: number) => {
    onChange(removeContactTelecomAt(contact, index));
  };

  const addFaxNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const updatedTelecom = [...telecom, { system: 'fax', value: trimmed } as any];
    onChange({ ...contact, telecom: updatedTelecom });
    setNewFaxValue('');
    setExpandingFax(false);
  };

  const setAvatar = async (file: File | null) => {
    if (!file) return;
    const avatar = await medplum.createAttachment({
      contentType: 'image/jpeg',
      data: file,
    });
    const extensions = contact.extension || [];
    const updatedExtensions = extensions.filter((e) => e.url !== ORGANIZATION_CONTACT_PHOTO_EXTENSION);
    updatedExtensions.push({
      url: ORGANIZATION_CONTACT_PHOTO_EXTENSION,
      valueAttachment: avatar,
    });
    onChange({ ...contact, extension: updatedExtensions });
  };

  const updateName = (value: string, field: 'given' | 'family') => {
    const nameToUpdate = contact.name || {};
    if (field === 'given') {
      nameToUpdate.given = [value];
    } else {
      nameToUpdate.family = value;
    }
    onChange({ ...contact, name: nameToUpdate });
    setName((prev) => ({ ...prev, [field]: value }));
  };

  const updateTitle = (value: string) => {
    const extensions = contact.extension || [];
    const updatedExtensions = extensions.filter((e) => e.url !== ORGANIZATION_CONTACT_TITLE_EXTENSION);
    if (value.trim()) {
      updatedExtensions.push({
        url: ORGANIZATION_CONTACT_TITLE_EXTENSION,
        valueString: value,
      });
    }
    onChange({ ...contact, extension: updatedExtensions });
    setTitle(value);
  };

  const resetFax = () => {
    setExpandingFax(false);
    setNewFaxValue('');
  };

  return (
    <Card withBorder radius="md" p="md" style={{ width: '100%' }}>
      <Menu>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xs"
            style={{ position: 'absolute', top: '10px', right: '10px' }}
          >
            <IconDots size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => onDelete()}>
            <Group gap="xs">
              <IconTrash color="red" size={16} />
              Delete
            </Group>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Group justify="space-between" align="flex-start">
        <Stack gap="xs" w="100%">
          <Group gap="md">
            <FileInput
              ref={avatarInputRef!}
              placeholder="Avatar"
              onChange={setAvatar}
              accept="image/*"
              display="none"
            />
            <Box onClick={() => avatarInputRef.current?.click()} style={{ cursor: 'pointer', flexShrink: 0 }}>
              <PartnerContactAvatar size="42" contact={contact} />
            </Box>
            <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
              <ConditionalLayout inlineCondition={!!(given && family)} gap={3}>
                <EditableField
                  value={given}
                  onSubmit={(value) => updateName(value, 'given')}
                  placeholder="Enter given name..."
                  inputProps={{ size: 'xs', w: given && family ? 'auto' : '100%' }}
                  textProps={{ size: 'sm', c: 'dimmed', style: { width: given && family ? 'auto' : '100%' } }}
                />
                <EditableField
                  value={family}
                  onSubmit={(value) => updateName(value, 'family')}
                  placeholder="Enter family name..."
                  inputProps={{ size: 'xs', w: given && family ? 'auto' : '100%' }}
                  textProps={{ size: 'sm', c: 'dimmed', style: { width: given && family ? 'auto' : '100%' } }}
                />
              </ConditionalLayout>
              <EditableField
                value={title || ''}
                onSubmit={(value) => updateTitle(value)}
                editable={true}
                inputProps={{
                  size: 'xs',
                  w: '100%',
                }}
                textProps={{
                  size: 'sm',
                  c: 'dimmed',
                  w: '100%',
                }}
              />
            </Stack>
          </Group>
          <Stack gap={4} mt={4} w="100%">
            <Group justify="space-between" align="center" w="100%">
              <Group gap={6}>
                <Text size="xs" c="gray">
                  Contact methods
                </Text>
              </Group>
              <ActionIcon
                onClick={() => setAddingNew((v) => !v)}
                variant="subtle"
                color="gray"
                size={16}
                radius="md"
                aria-label="Add contact method"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {nonFaxTelecom.map((t, index) => {
              const actualIndex = telecom.findIndex((tel) => tel === t);
              return (
                <TelecomEntry
                  key={t.id ?? `${t.system}-${index}`}
                  entry={t}
                  index={index}
                  actualIndex={actualIndex}
                  onUpdate={(actualIdx, val) => {
                    onChange(setContactTelecomAt(contact, actualIdx, val));
                  }}
                  onDelete={deleteTelecomAt}
                  options={{ dynamicSystem: true }}
                />
              );
            })}

            {addingNew && (
              <Group gap="xs" wrap="nowrap" w="100%" className={classes.aboutRow}>
                <SystemIcon system={inferSystem(newValue)} value={newValue} fallback={IconPhone} />
                <SmartEditableField
                  value={newValue}
                  placeholder="Enter phone, email, or website"
                  inputProps={{ size: 'xs', style: { width: '100%' } }}
                  editable={true}
                  focus={addingNew}
                  dynamicSystem={true}
                  onChange={(v) => setNewValue(v)}
                  onSubmit={async (v: string) => {
                    await addNewEntry(v);
                  }}
                  onCancel={resetFax}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      resetFax();
                    }
                  }}
                />
              </Group>
            )}
          </Stack>

          <Stack gap={4} w="100%">
            <Group justify="space-between" align="center" w="100%">
              <Text size="xs" c="gray">
                Fax number
              </Text>
              <ActionIcon
                onClick={() => setExpandingFax((v) => !v)}
                variant="subtle"
                color="gray"
                size={16}
                radius="md"
                aria-label="Add fax number"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {faxTelecoms.map((fax, index) => {
              const actualIndex = telecom.findIndex((tel) => tel === fax);
              return (
                <TelecomEntry
                  key={fax.id ?? `fax-${index}`}
                  entry={fax}
                  index={index}
                  actualIndex={actualIndex}
                  onUpdate={(actualIdx, val) => {
                    onChange(setContactTelecomAt(contact, actualIdx, val));
                  }}
                  onDelete={deleteTelecomAt}
                  options={{ dynamicSystem: false, placeholder: 'Enter fax number' }}
                />
              );
            })}

            {expandingFax && (
              <Group gap="xs" wrap="nowrap" w="100%" className={classes.aboutRow}>
                <SystemIcon system="fax" fallback={IconPhone} />
                <FaxEditableField
                  value={newFaxValue}
                  placeholder="+18136056242"
                  inputProps={{ size: 'xs', style: { width: '100%' } }}
                  editable={true}
                  focus={expandingFax}
                  onChange={(v) => setNewFaxValue(v)}
                  onSubmit={async (v) => {
                    addFaxNumber(v);
                  }}
                  onCancel={resetFax}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      resetFax();
                    }
                  }}
                />
              </Group>
            )}
          </Stack>
        </Stack>
      </Group>
    </Card>
  );
}
